import React from "react";
import { AlertTriangle, Inbox, Loader2, RefreshCw, Lock } from "lucide-react";
import { EVIDENCE_META, HEALTH_META, MODULE_STATUS_META, formatMetric } from "../lib/evidence";

// ---- evidence / status chips ----
export const EvidenceBadge = ({ state, className = "" }) => {
  const meta = EVIDENCE_META[state] || EVIDENCE_META.UNKNOWN;
  return (
    <span data-testid={`cc-evidence-${state}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.chip} ${className}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
      {meta.label}
    </span>
  );
};

export const HealthBadge = ({ state }) => {
  const meta = HEALTH_META[state] || HEALTH_META.UNKNOWN;
  return (
    <span data-testid={`cc-health-${state}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.chip}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
      {meta.label}
    </span>
  );
};

export const ModuleStatusBadge = ({ status }) => {
  const meta = MODULE_STATUS_META[status] || MODULE_STATUS_META.NOT_IMPLEMENTED;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-wider ${meta.chip}`}>
      {meta.label}
    </span>
  );
};

// ---- panel container ----
export const Panel = ({ title, subtitle, actions, children, testid }) => (
  <section data-testid={testid} className="bg-[#0f1420] border border-white/10 rounded-xl overflow-hidden">
    {(title || actions) && (
      <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-white/10">
        <div>
          {title && <h3 className="text-sm font-bold text-white tracking-wide">{title}</h3>}
          {subtitle && <p className="text-xs text-zinc-400 mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

// ---- metric card (evidence-aware; never fabricates a number) ----
export const MetricCard = ({ label, metric, hint }) => {
  const f = formatMetric(metric);
  return (
    <div data-testid={`cc-metric-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className="bg-[#141a28] border border-white/10 rounded-lg p-4 flex flex-col gap-2 hover:border-[#C5A059]/40 transition-colors">
      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">{label}</span>
      <span className={`text-2xl font-black tracking-tight ${f.isValue ? "text-white" : "text-zinc-500"}`}>
        {f.isValue ? f.display : <span className="text-base font-bold">{f.display}</span>}
      </span>
      <div className="flex items-center justify-between mt-auto pt-1">
        <EvidenceBadge state={f.state} />
        {metric?.sourceAuthority && <span className="text-[9px] uppercase tracking-wider text-zinc-500">{metric.sourceAuthority}</span>}
      </div>
      {hint && <p className="text-[10px] text-zinc-500">{hint}</p>}
    </div>
  );
};

export const MetricGrid = ({ metrics }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
    {metrics.map((m) => <MetricCard key={m.label} label={m.label} metric={m.metric} hint={m.hint} />)}
  </div>
);

// ---- states ----
export const Skeleton = ({ rows = 4 }) => (
  <div data-testid="cc-skeleton" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 animate-pulse">
    {Array.from({ length: rows * 2 }).map((_, i) => (
      <div key={i} className="bg-white/5 border border-white/5 rounded-lg h-28" />
    ))}
  </div>
);

export const LoadingBlock = () => (
  <div data-testid="cc-loading" className="flex items-center justify-center gap-2 py-12 text-zinc-400 text-sm">
    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
  </div>
);

export const EmptyState = ({ title = "No records", message }) => (
  <div data-testid="cc-empty" className="flex flex-col items-center justify-center gap-2 py-12 text-center">
    <Inbox className="w-8 h-8 text-zinc-600" />
    <p className="text-sm font-bold text-zinc-300">{title}</p>
    {message && <p className="text-xs text-zinc-500 max-w-sm">{message}</p>}
  </div>
);

export const ErrorState = ({ error, onRetry }) => {
  const restricted = error?.status === 403 || error?.code === "FORBIDDEN" || error?.code === "INVALID_SCOPE";
  return (
    <div data-testid="cc-error" role="alert" className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      {restricted ? <Lock className="w-8 h-8 text-red-400" /> : <AlertTriangle className="w-8 h-8 text-red-400" />}
      <div>
        <p className="text-sm font-bold text-red-300">{error?.code || "ERROR"}</p>
        <p className="text-xs text-zinc-400 max-w-sm mt-1">{error?.message || "Something went wrong."}</p>
      </div>
      {onRetry && !restricted && (
        <button onClick={onRetry} data-testid="cc-error-retry"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-black bg-[#C5A059] px-3 py-1.5 rounded-md hover:bg-white transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Retry
        </button>
      )}
    </div>
  );
};

// Wrap a panel body with truthful loading/error/empty handling.
export const AsyncPanelBody = ({ loading, error, empty, onRetry, children }) => {
  if (loading) return <Skeleton />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (empty) return <EmptyState {...(typeof empty === "object" ? empty : {})} />;
  return children;
};
